/**
 * The stock-based price curve (spec §5.1-§5.2, design §3.3): pure numbers in, numbers out, no store access and
 * no allocation (D-014). The worker's market (`economy/market.ts`) and the main thread's `StrategicMarket.quote`
 * behind M2's `quote()` (spec §5.6) both use these functions, so this file must stay importable by the main
 * thread: it imports only the `wares.json` market block.
 *
 * - `minPrice = minMult × base`, `maxPrice = maxMult × base` (base ±40%).
 * - Sell to a station: `S(fill) = lerp(maxPrice, minPrice, fill)` with `fill = stock / capacity`.
 * - Buy from a station: `B(fill') = buyMarkup × lerp(maxPrice, minPrice, fill')` with
 *   `fill' = (stock − reserved) / capacity`, so reservations raise the price others see.
 * - Totals for a quantity are exact (the curve is linear in stock): `q × S((stock + q/2) / capacity)` and
 *   `q × B((stock − reserved − q/2) / capacity)`, with `q` clamped to what fits.
 *
 * The multipliers, the player tolerance and the standing swing come from `wares.json` `market` (validated by
 * `data.ts`); `setPriceCurve` applies other values (tests, the dev panel).
 */
import waresJson from '../../data/strategic/wares.json'
import { SIDE } from '../types'

/** The tunable market values this file reads (the `wares.json` `market` block, or a subset of it). */
export interface PriceCurveConfig {
  minMult: number
  maxMult: number
  buyMarkup: number
  playerTolerance: number
  standingSwing: number
}

const DEFAULT_CURVE: Readonly<PriceCurveConfig> = {
  minMult: waresJson.market.minMult,
  maxMult: waresJson.market.maxMult,
  buyMarkup: waresJson.market.buyMarkup,
  playerTolerance: waresJson.market.playerTolerance,
  standingSwing: waresJson.market.standingSwing,
}

let minMult = DEFAULT_CURVE.minMult
let maxMult = DEFAULT_CURVE.maxMult
let buyMarkup = DEFAULT_CURVE.buyMarkup
let playerTolerance = DEFAULT_CURVE.playerTolerance
let standingSwing = DEFAULT_CURVE.standingSwing

/** Applies market values (missing keys keep their current value). Not for the hot loop. */
export function setPriceCurve(cfg: Partial<PriceCurveConfig>): void {
  if (cfg.minMult !== undefined) minMult = cfg.minMult
  if (cfg.maxMult !== undefined) maxMult = cfg.maxMult
  if (cfg.buyMarkup !== undefined) buyMarkup = cfg.buyMarkup
  if (cfg.playerTolerance !== undefined) playerTolerance = cfg.playerTolerance
  if (cfg.standingSwing !== undefined) standingSwing = cfg.standingSwing
}

/** Restores the `wares.json` values. */
export function resetPriceCurve(): void {
  setPriceCurve(DEFAULT_CURVE)
}

/** The values in use (a copy). */
export function priceCurve(): PriceCurveConfig {
  return { minMult, maxMult, buyMarkup, playerTolerance, standingSwing }
}

/** The player-trade tolerance on the unit price (spec §5.4: 2%). */
export function tradeTolerance(): number {
  return playerTolerance
}

function clamp01(x: number): number {
  return x > 0 ? (x < 1 ? x : 1) : 0
}

/** Fill for pricing: `amount / cap` clamped to 0..1 (0 without capacity). */
function fillOf(amount: number, cap: number): number {
  return cap > 0 ? clamp01(amount / cap) : 0
}

export function minPrice(base: number): number {
  return minMult * base
}

export function maxPrice(base: number): number {
  return maxMult * base
}

/** `S(fill)`: what a station pays per t at a fill (0..1, clamped). */
export function sellUnitAtFill(base: number, fill: number): number {
  const hi = maxMult * base
  return hi + (minMult * base - hi) * clamp01(fill)
}

/** `B(fill')`: what a station charges per t at an available fill (0..1, clamped). */
export function buyUnitAtFill(base: number, fill: number): number {
  return buyMarkup * sellUnitAtFill(base, fill)
}

/** `S`: the unit price a station pays for the next tonne sold to it. */
export function sellUnit(base: number, stock: number, cap: number): number {
  return sellUnitAtFill(base, fillOf(stock, cap))
}

/** `B`: the unit price a station charges for the next tonne bought from it (reservations raise it). */
export function buyUnit(base: number, stock: number, reserved: number, cap: number): number {
  return buyUnitAtFill(base, fillOf(stock - reserved, cap))
}

/** Tonnes of a sale of `q` that fit: `min(q, capacity − stock)`, never below 0. */
export function clampSellQty(stock: number, cap: number, q: number): number {
  const r = cap - stock
  const room = r > 0 ? r : 0
  return q > 0 ? (q < room ? q : room) : 0
}

/** Tonnes of a purchase of `q` available: `min(q, stock − reserved)`, never below 0. */
export function clampBuyQty(stock: number, reserved: number, q: number): number {
  const a = stock - reserved
  const avail = a > 0 ? a : 0
  return q > 0 ? (q < avail ? q : avail) : 0
}

/** Exact total a station pays for `q` t sold to it (`q` clamped to room): `q × S((stock + q/2) / cap)`. */
export function sellTotal(base: number, stock: number, cap: number, q: number): number {
  const qq = clampSellQty(stock, cap, q)
  if (qq <= 0) return 0
  return qq * sellUnitAtFill(base, fillOf(stock + qq * 0.5, cap))
}

/** Exact total a station charges for `q` t (`q` clamped to availability): `q × B((stock − reserved − q/2) / cap)`. */
export function buyTotal(base: number, stock: number, reserved: number, cap: number, q: number): number {
  const qq = clampBuyQty(stock, reserved, q)
  if (qq <= 0) return 0
  return qq * buyUnitAtFill(base, fillOf(stock - reserved - qq * 0.5, cap))
}

/** The projected fill traders score sinks by: `(stock + incoming) / capacity`, clamped to 0..1 (spec §5.3). */
export function projectedFill(stock: number, incoming: number, cap: number): number {
  return fillOf(stock + incoming, cap)
}

/**
 * M2 standing modifier (M2 spec §9.1: ±5% linear over standing −100..+100). Good standing raises what a
 * station pays the player (`side` = SIDE.sell) and lowers what it charges (`side` = SIDE.buy).
 */
export function standingMult(standing: number, side: number): number {
  // NaN (a missing standing) counts as neutral.
  const s = standing >= 100 ? 1 : standing <= -100 ? -1 : standing === standing ? standing / 100 : 0
  const swing = standingSwing * s
  return side === SIDE.sell ? 1 + swing : 1 - swing
}

/** True when `unit` is within the player tolerance of `expected` (relative to `expected`). */
export function withinTolerance(expected: number, unit: number): boolean {
  if (!(expected > 0)) return false
  const d = (unit - expected) / expected
  return (d < 0 ? -d : d) <= playerTolerance + 1e-12
}
